import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Heading3 } from '@/components/ui/typography'
import { State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridColumnProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { SearchResultDto, SearchResultType } from 'api/Search/Types/SearchResultDto'
import classNames from 'classnames'
import { defineTab, SearchTab } from 'components/SearchTab/SearchTab'
import { Check, Form, LayoutDashboard } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTransactionTypeAction } from 'store/transactions/transaction.action'
import { isEmpty } from 'utils/is-empty'

import { Spinner } from '../../components/Spinner'
import { useApiClient } from '../../utils/api/ApiClient'
import { SearchApiClient } from '../../utils/api/searchApi'
import { WalletsApiClient } from '../../utils/api/walletsApiClient'
import { AffiliateColumnDefinition } from './AffiliateColumnDefinition'
import { ClientColumnDefinition } from './ClientColumnDefinition'
import { IntroducingBrokerColumnDefinition } from './IntroducingBrokerColumnDefinition'
import { LeadColumnDefinition } from './LeadColumnDefinition'
import { TradingAccountColumnDefinition } from './TradingAcountColumnDefinition'
import { TransactionColumnDefinition } from './TransactionColumnDefinition'
import { WalletColumnDefinition } from './WalletColumnDefinition'

import styles from './SearchPage.module.scss'

export type SearchItemType = keyof SearchResultType

interface SearchResultTypeDefinition {
  columns: () => GridColumnProps[]
}

const SearchItemTypes: Record<SearchItemType, SearchResultTypeDefinition> = {
  client: {
    columns: () => ClientColumnDefinition,
  },
  lead: {
    columns: () => LeadColumnDefinition,
  },
  introducing_broker: {
    columns: () => IntroducingBrokerColumnDefinition,
  },
  trading_account: {
    columns: () => TradingAccountColumnDefinition,
  },
  transaction: {
    columns: () => TransactionColumnDefinition,
  },
  wallet: {
    columns: () => WalletColumnDefinition,
  },
  affiliate: {
    columns: () => AffiliateColumnDefinition,
  },
}

const createDataState = (state: State, result: SearchResultDto[], total: number) => ({
  result,
  dataState: state,
  total,
})

interface SectionComponentProps {
  q?: string
  id: keyof SearchResultType
  title: string
  className: string
}

const SectionComponent: React.FC<SectionComponentProps> = ({ q, id, title, className }) => {
  const searchApiClient = useApiClient(SearchApiClient)
  const [dataState, setDataState] = useState<{
    result: SearchResultDto[]
    dataState: State
    total: number
  }>(createDataState({ skip: 0 }, [], 0))
  const [isLoading, setIsLoading] = useState(false)
  const [isSearchComplete, setIsSearchComplete] = useState(false)
  const lastQueryRef = useRef<string | undefined>()

  useEffect(() => {
    const fetchData = async () => {
      if (q && q !== lastQueryRef.current) {
        setIsLoading(true)
        setIsSearchComplete(false)
        lastQueryRef.current = q
        try {
          const response = await searchApiClient.getSearchResults({
            text: q,
            type: id,
            pageSize: 10,
            pageIndex: 1,
          })
          setDataState(createDataState({ skip: 0 }, response.items, response.totalResults))
        } catch (error) {
          console.error(error)
        } finally {
          setIsLoading(false)
          setIsSearchComplete(true)
        }
      } else if (!q) {
        setDataState(createDataState({ skip: 0 }, [], 0))
        setIsSearchComplete(false)
        lastQueryRef.current = undefined
      }
    }

    fetchData()
  }, [q, id, searchApiClient])

  const pageChange = async (event: GridPageChangeEvent) => {
    if (q) {
      setIsLoading(true)
      try {
        const response = await searchApiClient.getSearchResults({
          text: q,
          type: id,
          pageSize: 10,
          pageIndex: (event.page.skip + event.page.take) / 10,
        })
        setDataState(
          createDataState(
            { ...dataState.dataState, skip: event.page.skip },
            response.items,
            response.totalResults
          )
        )
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const typeDefinition = SearchItemTypes[id]

  return (
    <div className={classNames(styles.sectionContainer, className)}>
      <Heading3 className='flex items-center font-semibold gap-2 mb-2'>
        {title}
        {isSearchComplete && <Check size={32} className='text-green-600' />}
      </Heading3>
      {isLoading ? (
        <Spinner />
      ) : (
        <Grid
          className={className}
          data={dataState.result}
          take={10}
          skip={dataState.dataState.skip}
          onPageChange={pageChange}
          total={dataState.total}
          pageable
        >
          {typeDefinition.columns().map((column, idx) => (
            <Column
              key={idx + 2}
              field={column.field}
              title={column.title}
              cell={column.cell}
              width={column.width}
              minResizableWidth={column.minResizableWidth}
              className={column.className}
            />
          ))}
        </Grid>
      )}
    </div>
  )
}

export type SectionId =
  | ''
  | 'client'
  | 'wallet'
  | 'trading_account'
  | 'transaction'
  | 'introducing_broker'
  | 'lead'
  | 'affiliate'

interface SectionDefinition {
  id: SectionId
  title: string
}

const SearchPage = (): JSX.Element => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const q = searchParams.get('q') || undefined
  const sectionParam = searchParams.get('section') as SectionId | null
  const [refreshTrigger, setRefreshTrigger] = useState<Record<SectionId, number>>({
    client: 0,
    wallet: 0,
    trading_account: 0,
    transaction: 0,
    introducing_broker: 0,
    lead: 0,
    affiliate: 0,
    '': 0,
  })

  const searchPageLayoutPreference = 'searchPageLayoutPreference '

  const [isGridView, setIsGridView] = useState(
    localStorage.getItem(searchPageLayoutPreference) === 'true'
  )

  const transactionTypes = useAppSelector((state) => state.transactions.types)
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)

  useEffect(() => {
    if (isEmpty(transactionTypes)) {
      dispatch(fetchTransactionTypeAction(walletsApiClient))
    }
  }, [dispatch, transactionTypes, walletsApiClient])

  const sections: SectionDefinition[] = [
    { id: 'client', title: 'Clients' },
    { id: 'wallet', title: 'Wallets' },
    { id: 'trading_account', title: 'Trading Accounts' },
    { id: 'transaction', title: 'Transactions' },
    { id: 'introducing_broker', title: 'Introducing Broker' },
    { id: 'lead', title: 'Leads' },
    { id: 'affiliate', title: 'Affiliates' },
  ]

  const [sectionFilter, setSectionFilter] = useState<SectionId[]>(
    sectionParam ? [sectionParam] : []
  )

  useEffect(() => {
    setSectionFilter(sectionParam ? [sectionParam] : [])
  }, [sectionParam])

  const onSectionFilterChanged = (newFilter: string[] | SectionId[]) => {
    setSectionFilter(newFilter.includes('') ? [] : (newFilter as SectionId[]))
  }

  const activeSections =
    sectionFilter.length === 0
      ? sections
      : sections.filter((section) => sectionFilter.includes(section.id))

  const sectionTabs = [
    defineTab('', 'All'),
    ...sections.map((section) => defineTab(section.id, section.title)),
  ]

  const handleRefresh = () => {
    setRefreshTrigger((prev) => {
      const newTrigger = { ...prev }
      activeSections.forEach((section) => {
        newTrigger[section.id] = prev[section.id] + 1
      })
      return newTrigger
    })
  }

  const handleSetIsGridView = (isGridView: boolean) => {
    localStorage.setItem(searchPageLayoutPreference, `${isGridView}`)

    setIsGridView(isGridView)
  }

  return (
    <main className={styles.container}>
      <div className={styles.searchHeader}>
        <SearchTab
          tabs={sectionTabs}
          currentTabs={sectionFilter}
          onTabChanged={onSectionFilterChanged}
        />

        <div className='flex gap-2'>
          <ToggleGroup variant='outline' defaultValue={[isGridView ? 'grid' : 'list']}>
            <ToggleGroupItem
              value='grid'
              onClick={() => handleSetIsGridView(true)}
              aria-label='Grid'
            >
              <LayoutDashboard />
              Grid
            </ToggleGroupItem>
            <ToggleGroupItem
              value='list'
              onClick={() => handleSetIsGridView(false)}
              aria-label='List'
            >
              <Form />
              List
            </ToggleGroupItem>
          </ToggleGroup>

          <Button onClick={handleRefresh}>Refresh Results</Button>
        </div>
      </div>
      <div className={classNames({ [styles.gridView]: isGridView })}>
        {activeSections.map(({ id, title }) => (
          <SectionComponent
            className={classNames({ [styles.gridItem]: isGridView })}
            key={`${id}-${q}-${refreshTrigger[id]}`}
            id={id as keyof SearchResultType}
            title={title}
            q={q}
          />
        ))}
      </div>
    </main>
  )
}

export default SearchPage
